/**
 * Etichete pentru importul în registru: tipuri de document (DocType din
 * packages/doc-parsers), coduri de alertă (validation-rules din patient-service)
 * și câmpurile extrase (ExtractedFields).
 */

export const DOC_TYPE_LABELS: Record<string, string> = {
  chestionar: 'Chestionar',
  'bilet-iesire': 'Bilet de ieșire',
  'foaie-transfer': 'Foaie de transfer',
  'foaie-observatie': 'Foaie de observație',
  histopatologie: 'Rezultat HP',
  'lipsa-hp': 'Lipsă HP',
  'scrisoare-medicala': 'Scrisoare medicală',
  analize: 'Analize',
  'consult-oncologie': 'Consult oncologie',
  imagistica: 'Imagistică (RMN/IRM)',
  fotofinder: 'FotoFinder',
  necunoscut: 'Neclasificat',
  UNKNOWN: 'Neclasificat',
};

/** Tipurile între care operatorul poate reclasifica un document. */
export const RECLASSIFY_OPTIONS = Object.entries(DOC_TYPE_LABELS)
  .filter(([key]) => key !== 'UNKNOWN')
  .map(([value, label]) => ({ value, label }));

export const ALERT_CODE_LABELS: Record<string, string> = {
  CNP_INVALID_OR_MISSING: 'CNP invalid sau lipsă',
  CNP_MISMATCH: 'CNP diferit între documente',
  NAME_MISMATCH: 'Nume nepotrivit cu folderul',
  MISSING_REQUIRED_DOCUMENT: 'Lipsește biletul sau chestionarul',
  HP_BULLETIN_CONFLICT: 'Nr. buletin HP folosit la alt pacient',
  SEX_OR_BIRTHDATE_MISMATCH: 'Sex / dată naștere diferite de CNP',
  DISCHARGE_DX_DIFFERS_FROM_HP: 'Diagnosticul la externare diferă de HP',
  HP_NEGATIVE_OR_IHC_PENDING: 'HP negativ sau IHC în așteptare',
  DATE_ORDER: 'Ordinea datelor e neobișnuită',
  CONSENT_SIGNED_NO_TICKS: 'Consimțământ semnat fără bife',
  UNCLASSIFIED_DOCUMENT: 'Document neclasificat',
};

export const ENGINE_LABELS: Record<string, string> = {
  TESSERACT: 'Tesseract OCR',
  GEMINI: 'Tesseract + Gemini (fallback)',
  NONE: 'Fără extracție',
};

export const CONSENT_OPTIONS = [
  { value: 'TICKED', label: 'Bifat' },
  { value: 'SIGNED_NO_TICKS', label: 'Semnat fără bife' },
  { value: 'MISSING', label: 'Lipsă' },
];

/** Etichetele câmpurilor extrase (ExtractedFields). */
export const FIELD_LABELS: Record<string, string> = {
  cnp: 'CNP',
  fullName: 'Nume complet',
  sex: 'Sex',
  birthDate: 'Data nașterii',
  age: 'Vârsta',
  address: 'Adresă',
  city: 'Localitate',
  county: 'Județ',
  environment: 'Mediu (U/R)',
  hospital: 'Spital',
  unit: 'Secția',
  observationSheetNo: 'Nr. foaie de observație',
  admissionDate: 'Data internării',
  dischargeDate: 'Data externării',
  admissionType: 'Tip internare',
  principalDiagnosisText: 'Diagnostic principal',
  admissionDiagnosisText: 'Diagnostic la internare',
  diagnosisLines: 'Diagnostice (câte unul pe rând)',
  principalIcd10: 'Cod ICD-10 principal',
  secondaryIcd10: 'Coduri ICD-10 secundare (virgulă)',
  secondaryDiagnosisTexts: 'Diagnostice secundare (câte unul pe rând)',
  epicrisis: 'Epicriză',
  recommendations: 'Recomandări',
  hpBulletinNo: 'Nr. buletin HP',
  hpCollectedDate: 'Data recoltării',
  hpReceivedDate: 'Data primirii probei',
  hpResultDate: 'Data rezultatului HP',
  hpSpecimen: 'Proveniența materialului / localizare',
  hpClinicalDiagnosis: 'Diagnostic clinic (pe buletin)',
  hpMacroscopy: 'Examen macroscopic',
  hpMicroscopy: 'Examen microscopic',
  hpConclusion: 'Concluzie HP',
  hpTopographyIcd10: 'Cod localizare (ICD-10)',
  icdO3Morphology: 'Cod morfologie (ICD-O-3)',
  hpPathologist: 'Medic anatomopatolog',
  hpIhc: 'Imunohistochimie',
  hpMargins: 'Margini (FREE / CLOSE / INVOLVED)',
  breslowMm: 'Breslow (mm)',
  clarkLevel: 'Nivel Clark',
  ulceration: 'Ulcerație',
  hpMissing: 'Buletin fără rezultat',
  collectedAt: 'Data recoltării analizelor',
  consultDate: 'Data consultului',
  physician: 'Medic',
  specialty: 'Specialitate',
  presentationReason: 'Motivul prezentării',
  documentDate: 'Data documentului',
  consentStatus: 'Consimțământ',
};

const IDENTITY = ['cnp', 'fullName'];
const HP = [
  'hpBulletinNo',
  'hpCollectedDate',
  'hpResultDate',
  'hpSpecimen',
  'hpConclusion',
  'hpTopographyIcd10',
  'icdO3Morphology',
  'hpMargins',
  'breslowMm',
  'clarkLevel',
  'hpIhc',
  'hpPathologist',
];

/** Câmpurile afișate (în ordine) pentru fiecare tip de document. */
export const FIELDS_BY_DOC_TYPE: Record<string, string[]> = {
  'bilet-iesire': [
    ...IDENTITY,
    'unit',
    'observationSheetNo',
    'admissionDate',
    'dischargeDate',
    'principalDiagnosisText',
    'diagnosisLines',
    'principalIcd10',
    'epicrisis',
    'recommendations',
    ...HP,
  ],
  'foaie-transfer': [
    ...IDENTITY,
    'unit',
    'county',
    'city',
    'environment',
    'admissionType',
    'admissionDiagnosisText',
    'principalDiagnosisText',
    'principalIcd10',
    'secondaryDiagnosisTexts',
    'secondaryIcd10',
    'admissionDate',
    'dischargeDate',
  ],
  histopatologie: [...IDENTITY, 'sex', 'age', ...HP, 'hpClinicalDiagnosis', 'hpMicroscopy', 'hpMacroscopy'],
  'lipsa-hp': [...IDENTITY, 'hpMissing'],
  'scrisoare-medicala': [
    ...IDENTITY,
    'birthDate',
    'consultDate',
    'unit',
    'presentationReason',
    'principalDiagnosisText',
    'principalIcd10',
    'secondaryIcd10',
    'recommendations',
    'physician',
  ],
  analize: [...IDENTITY, 'collectedAt'],
  'consult-oncologie': ['consultDate', 'epicrisis', 'recommendations', 'physician'],
  chestionar: ['consentStatus', 'cnp', 'fullName', 'birthDate'],
  imagistica: ['documentDate'],
  fotofinder: ['documentDate'],
};
FIELDS_BY_DOC_TYPE['foaie-observatie'] = FIELDS_BY_DOC_TYPE['foaie-transfer'];

/** Câmpurile cu text lung (TextArea). */
export const LONG_TEXT_FIELDS = new Set([
  'epicrisis',
  'recommendations',
  'hpConclusion',
  'hpMicroscopy',
  'hpMacroscopy',
  'diagnosisLines',
  'secondaryDiagnosisTexts',
  'principalDiagnosisText',
  'admissionDiagnosisText',
]);
